import React, { useState, useEffect } from 'react';
import { downloadWordDocument } from '../api/wordDocuments';
import { Download } from 'lucide-react';

interface WordPreviewProps {
  tenantId: string;
  documentId: string;
  onClose?: () => void;
  title?: string;
  previewType?: 'generated' | 'template';
  fileName?: string;
}

const HEADER_HEIGHT = 56;
const MAX_FETCH_RETRIES = 2; // 2 próby po pierwszym fetchu = łącznie 3 szanse
const FETCH_RETRY_DELAY = 1000; // 1 sekunda między próbami fetchu
const IFRAME_TIMEOUT = 10000; // 10 sekund na załadowanie iframe
const MAX_IFRAME_RETRIES = 2; // 2 próby po pierwszym razie = 3 szanse

const WordPreview: React.FC<WordPreviewProps> = ({ tenantId, documentId, onClose, title, previewType = 'generated', fileName }) => {
  const [viewerUrl, setViewerUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchRetryCount, setFetchRetryCount] = useState<number>(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeTimeoutError, setIframeTimeoutError] = useState(false);
  const [iframeRetryCount, setIframeRetryCount] = useState(0);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const iframeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isOpeningNewWindow, setIsOpeningNewWindow] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Spinner CSS
  const spinnerStyle: React.CSSProperties = {
    width: 60,
    height: 60,
    border: '6px solid #e0e7ef',
    borderTop: '6px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  // Pobieranie linku do podglądu (retry tylko na fetch)
  const getPreviewEndpoint = () => {
    if (previewType === 'template') {
      return `/api/tenants/${tenantId}/words/preview-url/${documentId}`;
    }
    return `/api/tenants/${tenantId}/generated-documents/preview-url/${documentId}`;
  };

  // Funkcja do równoległego pobierania kilku linków preview-url (Promise.race)
  async function fetchPreviewUrlRace(getPreviewEndpoint: () => string, attempts = 3, timeoutMs = 4000): Promise<string | null> {
    const fetchWithTimeout = () =>
      new Promise<string | null>((resolve) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
          controller.abort();
          resolve(null);
        }, timeoutMs);
        fetch(getPreviewEndpoint(), { signal: controller.signal })
          .then(res => res.json())
          .then(data => {
            clearTimeout(timeout);
            if (data && data.url) resolve(data.url);
            else resolve(null);
          })
          .catch(() => {
            clearTimeout(timeout);
            resolve(null);
          });
      });
    const promises = Array.from({ length: attempts }, () => fetchWithTimeout());
    const first = await Promise.race(promises);
    return first;
  }

  // Retry tylko na fetch (teraz z równoległym pobieraniem)
  useEffect(() => {
    setViewerUrl('');
    setFetchRetryCount(0);
    setIframeLoaded(false);
    setIframeTimeoutError(false);
    setIframeRetryCount(0); // RESETUJEMY retry iframe przy każdej nowej próbie
    let cancelled = false;
    async function tryFetch(attempt: number) {
      const url = await fetchPreviewUrlRace(getPreviewEndpoint, 3, 4000);
      if (cancelled) return;
      if (url) {
        const encodedUrl = encodeURIComponent(url);
        setViewerUrl(`https://docs.google.com/gview?url=${encodedUrl}&embedded=true`);
        setError(null);
      } else {
        if (attempt < MAX_FETCH_RETRIES) {
          setTimeout(() => tryFetch(attempt + 1), FETCH_RETRY_DELAY);
        } else {
          setError('Nie udało się pobrać linku do podglądu. Spróbuj ponownie później.');
        }
      }
      setLoading(false);
    }
    tryFetch(0);
    // cleanup
    return () => {
      cancelled = true;
      setViewerUrl('');
      setIframeLoaded(false);
      setIframeTimeoutError(false);
      setIframeRetryCount(0);
    };
  }, [tenantId, documentId, previewType]);

  // Retry na timeout iframe (np. 204/no content) – natychmiast pobierz nowy link (do 3 prób)
  useEffect(() => {
    if (iframeTimeoutError && iframeRetryCount < MAX_IFRAME_RETRIES && !error) {
      setIframeRetryCount(c => c + 1);
      setViewerUrl('');
      setTimeout(async () => {
        const url = await fetchPreviewUrlRace(getPreviewEndpoint, 3, 4000);
        if (url) {
          const encodedUrl = encodeURIComponent(url);
          setViewerUrl(`https://docs.google.com/gview?url=${encodedUrl}&embedded=true`);
          setError(null);
        }
      }, 200); // szybka próba
    }
  }, [iframeTimeoutError, iframeRetryCount, error, fetchPreviewUrlRace]);

  // Timeout na iframe (nie ponawiaj fetchu, tylko pokaż błąd)
  useEffect(() => {
    if (!viewerUrl || error) return;
    setIframeLoaded(false);
    setIframeTimeoutError(false);
    if (iframeTimeoutRef.current) clearTimeout(iframeTimeoutRef.current);
    iframeTimeoutRef.current = setTimeout(() => {
      if (!iframeLoaded) {
        setIframeTimeoutError(true);
      }
    }, IFRAME_TIMEOUT);
    return () => {
      if (iframeTimeoutRef.current) clearTimeout(iframeTimeoutRef.current);
    };
  }, [viewerUrl, error]);

  // Gdy iframe się załaduje, wyczyść timeout
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setIframeTimeoutError(false);
    if (iframeTimeoutRef.current) clearTimeout(iframeTimeoutRef.current);
  };

  // Gdy iframe zgłosi błąd
  const handleIframeError = () => {
    setIframeTimeoutError(true);
    setIframeLoaded(false);
    if (iframeTimeoutRef.current) clearTimeout(iframeTimeoutRef.current);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
        background: '#232323',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      {/* HEADER JAK W GMAIL */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: HEADER_HEIGHT,
          background: '#202124',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          zIndex: 10001,
          boxShadow: '0 1px 0 #232323',
          padding: 0,
          userSelect: 'none',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
        }}>
          <img
            src={require('../word.svg').default}
            alt="Word logo"
            style={{ height: 32, width: 32, marginLeft: 24, marginRight: 16, flexShrink: 0 }}
          />
          <span style={{
            fontWeight: 700,
            fontSize: 19,
            color: '#fff',
            letterSpacing: '0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            minWidth: 0,
          }}>
            {title || 'Podgląd dokumentu'}
          </span>
        </div>
        <div style={{
          position: 'absolute',
          right: 36,
          top: 0,
          height: HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <a
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#2563eb',
              color: '#fff',
              borderRadius: 8,
              padding: '7px 14px',
              fontWeight: 600,
              fontSize: 15,
              textDecoration: 'none',
              boxShadow: '0 2px 8px #2563eb22',
              transition: 'background 0.15s',
              pointerEvents: isOpeningNewWindow ? 'none' : (viewerUrl ? 'auto' : 'none'),
              opacity: isOpeningNewWindow ? 0.7 : (viewerUrl ? 1 : 0.5),
            }}
            onClick={async e => {
              e.preventDefault();
              e.stopPropagation();
              if (isOpeningNewWindow) return;
              setIsOpeningNewWindow(true);
              try {
                const res = await fetch(getPreviewEndpoint());
                const data = await res.json();
                if (data && data.url) {
                  const encodedUrl = encodeURIComponent(data.url);
                  const newViewerUrl = `https://docs.google.com/gview?url=${encodedUrl}`;
                  window.open(newViewerUrl, '_blank');
                } else {
                  alert('Nie udało się pobrać nowego linku do podglądu.');
                }
              } catch {
                alert('Błąd pobierania nowego linku do podglądu.');
              } finally {
                setIsOpeningNewWindow(false);
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            {isOpeningNewWindow ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 16, height: 16, border: '2px solid #e0e7ef', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                Otwieranie...
              </span>
            ) : (
              'Otwórz w nowym oknie'
            )}
          </a>
          <button
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#2563eb',
              color: '#fff',
              borderRadius: 8,
              padding: '7px 14px',
              fontWeight: 600,
              fontSize: 15,
              textDecoration: 'none',
              boxShadow: '0 2px 8px #2563eb22',
              transition: 'background 0.15s',
              border: 'none',
              cursor: isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
            }}
            title="Pobierz dokument Word"
            onClick={async e => {
              e.preventDefault();
              e.stopPropagation();
              if (isDownloading) return;
              setIsDownloading(true);
              setDownloadError(null);
              try {
                const response = await fetch(`/api/tenants/${tenantId}/generated-documents/download/${documentId}`, { credentials: 'include' });
                if (!response.ok) throw new Error('Błąd pobierania pliku.');
                const disposition = response.headers.get('Content-Disposition');
                let downloadFileName = (typeof title === 'string' && title.trim()) ? title.trim() : (typeof fileName === 'string' && fileName.trim() ? fileName.trim() : `dokument-${documentId}.docx`);
                if (disposition) {
                  const match = disposition.match(/filename="?([^";]+)"?/);
                  if (match && match[1]) {
                    downloadFileName = decodeURIComponent(match[1]);
                  }
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = downloadFileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (err) {
                setDownloadError('Błąd pobierania pliku.');
              } finally {
                setIsDownloading(false);
              }
            }}
          >
            <Download size={18} style={{ marginRight: 6 }} />
            {isDownloading ? 'Pobieranie...' : 'Pobierz'}
          </button>
          {downloadError && (
            <span style={{ color: '#ef4444', fontWeight: 500, fontSize: 14, marginLeft: 10 }}>{downloadError}</span>
          )}
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 6,
            borderRadius: 8,
            transition: 'background 0.15s',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }} title="Zamknij podgląd" aria-label="Zamknij podgląd">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
      {/* KARTKA NA CAŁĄ SZEROKOŚĆ */}
      <div
        style={{
          position: 'absolute',
          top: HEADER_HEIGHT,
          left: 0,
          width: '100vw',
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          background: 'transparent',
          overflow: 'auto',
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: '#fff',
            boxShadow: '0 0 24px 0 rgba(60,64,67,0.13)',
            borderRadius: 0,
            width: '100vw',
            height: '100%',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            position: 'relative'
          }}
          onClick={e => e.stopPropagation()}
        >
          {(loading || (!viewerUrl && !error)) && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              zIndex: 2
            }}>
              <div style={spinnerStyle}></div>
            </div>
          )}
          {(error || iframeTimeoutError) && (
            <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 20, background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 8px 40px rgba(60,64,67,0.13)' }}>
              {error || 'Nie udało się załadować podglądu dokumentu (przekroczono czas oczekiwania). Spróbuj odświeżyć stronę lub wrócić później.'}
            </div>
          )}
          {!loading && !error && !iframeTimeoutError && viewerUrl && (
            <>
              {!iframeLoaded && (
                <div style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  zIndex: 2
                }}>
                  <div style={spinnerStyle}></div>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={viewerUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  background: '#fff',
                  display: iframeLoaded ? 'block' : 'none',
                  opacity: iframeLoaded ? 1 : 0,
                  transition: 'opacity 0.3s'
                }}
                title="Podgląd dokumentu Word"
                allowFullScreen
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            </>
          )}
        </div>
      </div>
      <style>
        {`
          .word-preview-modal * {
            box-shadow: none !important;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default WordPreview; 